import { useState, useEffect, useCallback, useRef } from 'react';
import DeliveryEstimateServ from '../service/DeliveryEstimateServ';
import dayjs from 'dayjs';
import { formatDeliveryTime } from '../util/unitFormatter';

/**
 * Hook to fetch and manage delivery estimates for a shop given a user's location
 * @param {string|string[]} shopIds - Single or multiple shop IDs
 * @param {number} latitude - User latitude
 * @param {number} longitude - User longitude
 * @param {string} addressId - Optional selected address ID
 */
export const useDeliveryEstimate = (shopIds, latitude, longitude, addressId = null, deliveryType = 'Normal') => {
    const [estimates, setEstimates] = useState({});
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchEstimates = useCallback(async () => {
        if (!shopIds || (Array.isArray(shopIds) && shopIds.length === 0)) return;

        // Allow fetch if we have coordinates OR an addressId
        if (!addressId && (!latitude || !longitude)) return;

        try {
            setLoading(true);
            const sIds = Array.isArray(shopIds) ? shopIds : [shopIds];

            const params = {
                shopId: sIds,
                latitude,
                longitude,
                addressId,
                deliveryType,
                currentTime: new Date().toISOString()
            };
            const res = await DeliveryEstimateServ.getEstimate(params);

            const resultsMap = {};
            if (res?.data) {
                res.data.forEach(item => {
                    // Normalize to lowercase to match formatETA lookup
                    resultsMap[item.shopId?.toString().toLowerCase()] = item;
                });
            }

            setEstimates(resultsMap);
            setSummary(res?.summary || null);
            setError(null);
        } catch (err) {
            console.error("Delivery Estimate Hook Error:", err);
            setError(err);
        } finally {
            setLoading(false);
        }
    }, [shopIds, latitude, longitude, addressId, deliveryType]);

    useEffect(() => {
        fetchEstimates();
    }, [fetchEstimates]);

    const formatETA = (shopId, fallbackEta = null) => {
        const sIdStr = shopId?.toString().toLowerCase();
        // The cache/estimates keys are set to lowercase strings
        const est = estimates[sIdStr];

        if (!est) {
            return (loading && sIdStr) ? 'Estimating...' : fallbackEta;
        }

        if (est.status === 'not_available' || est.error || (!est.eta && !est.deliveryTime)) {
            return fallbackEta;
        }

        if (est.deliveryTime !== undefined && est.deliveryTime !== null) {
            // deliveryTime may be a number (minutes) or a range string from the backend
            const dt = est.deliveryTime;
            if (typeof dt === 'number') {
                return formatDeliveryTime(`${dt} mins`);
            }
            return formatDeliveryTime(dt); 
        }

        const eta = dayjs(est.eta);
        const now = dayjs();
        const diffMinutes = Math.max(1, eta.diff(now, 'minute'));

        if (diffMinutes >= 1440) { // 24 hours * 60 minutes
            const days = Math.round(diffMinutes / 1440);
            return `${days} day${days > 1 ? 's' : ''}`;
        } 
        
        return formatDeliveryTime(`${diffMinutes} mins`);
    };

    return { estimates, summary, loading, error, formatETA, refresh: fetchEstimates };
};
